/**
 * useModalDismiss — 모달 닫기/스크롤락/포커스 트랩 공통 훅
 * ----------------------------------------------------------------------------
 * 보고서 §5.4 / §8.3: 9종 모달 중 ESC 닫기 0개, 외부클릭 닫기 2개뿐 → 표준화.
 *
 * - dismissOnEsc(기본 true): ESC 키로 onClose
 * - dismissOnBackdrop(기본 false): 백드롭 클릭으로 닫기 (기존에 없던 모달이 다수라 안전 기본값)
 * - 열려 있는 동안 body 스크롤 락, 언마운트/닫힘 시 복원
 * - 포커스 트랩: Tab 이 패널 밖으로 나가지 않게 순환
 *
 * ★ 열린 창 스택(모달·오른쪽 패널·확인창 공용):
 *   - ESC 는 **맨 위 창만** 닫는다. 예전엔 document 리스너가 전부 반응해 확인창 위에서 ESC 한 번에 아래 모달까지 닫혔다.
 *   - z-index 도 스택 순서로 매긴다(zIndex 반환). 나중에 연 창이 항상 위 — 오른쪽 패널(SidePanel) 안에서 연
 *     모달·확인창이 패널 밑에 깔리던 문제를 없앤다.
 * ★ 포커스: 열릴 때 이미 창 안에 포커스가 있으면(autoFocus) 그대로 두고, 없으면 첫 입력칸 → 첫 포커스 요소 순.
 *
 * 사용 예:
 *   const { backdropProps, panelRef, zIndex } = useModalDismiss({ open, onClose });
 */
import React from "react";

/** 열린 창 스택 — 순서가 곧 쌓임 순서다. */
const openStack: number[] = [];
let nextToken = 1;
/** 스택 바닥 z — 앱의 고정 요소(상단 바·사이드바·옛 오버레이 9999)보다 위 */
const LAYER_BASE = 10010;
const LAYER_STEP = 10;

type Options = {
  open: boolean;
  onClose: () => void;
  dismissOnEsc?: boolean;
  dismissOnBackdrop?: boolean;
};

export function useModalDismiss({
  open,
  onClose,
  dismissOnEsc = true,
  dismissOnBackdrop = false,
}: Options) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const tokenRef = React.useRef<number>(0);
  const [depth, setDepth] = React.useState(0);

  // 스택 등록 — 열려 있는 동안만
  React.useEffect(() => {
    if (!open) return;
    const token = nextToken++;
    tokenRef.current = token;
    openStack.push(token);
    setDepth(openStack.length);
    return () => {
      const i = openStack.indexOf(token);
      if (i >= 0) openStack.splice(i, 1);
    };
  }, [open]);

  // ESC 닫기
  // ★ onClose 는 ref 로 든다 — 인라인 화살표 함수면 렌더마다 리스너가 다시 붙어 등록 순서가 뒤섞였다.
  // ★ 한 번의 ESC 는 한 창만 닫는다 — 맨 위 창이 닫히며 스택에서 빠진 뒤(마이크로태스크) 다음 리스너가
  //   "이제 내가 맨 위"로 착각해 아래 창까지 닫던 틈을 이벤트 표시로 막는다.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  React.useEffect(() => {
    if (!open || !dismissOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const marked = e as KeyboardEvent & { __vaultModalHandled?: boolean };
      if (marked.__vaultModalHandled) return;
      // 맨 위 창만 반응한다(스택이 비었으면 — 등록 전 첫 렌더 — 예전처럼 닫는다).
      const top = openStack[openStack.length - 1];
      if (top !== undefined && top !== tokenRef.current) return;
      marked.__vaultModalHandled = true;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissOnEsc]);

  // body 스크롤 락
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // 포커스 트랩 + 열릴 때 패널로 포커스 이동
  React.useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusables = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

    // autoFocus 로 이미 창 안에 포커스가 있으면 그대로 둔다(예전엔 닫기 X 로 빼앗았다).
    if (!panel.contains(document.activeElement)) {
      const input = panel.querySelector<HTMLElement>(
        '[data-autofocus], input:not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled])'
      );
      (input && input.offsetParent !== null ? input : focusables()[0])?.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    panel.addEventListener("keydown", onKey);
    return () => panel.removeEventListener("keydown", onKey);
  }, [open]);

  const backdropProps = {
    onMouseDown: (e: React.MouseEvent) => {
      // 백드롭 자체를 직접 눌렀을 때만 (패널 내부 드래그-아웃 무시)
      if (dismissOnBackdrop && e.target === e.currentTarget) {
        onClose();
      }
    },
  };

  const zIndex = LAYER_BASE + Math.max(depth, 1) * LAYER_STEP;
  return { backdropProps, panelRef, zIndex };
}
